import React, { useState, useEffect, createRef, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { AppBar, NavItem, Toolbar } from '@groma-dev/navigation';
import {
  ArrowLeftIcon,
  classNames,
  CloseIcon,
  FullLightLogo,
  getFocusableElements,
  getTestIdAttribute,
  MenuIcon,
  setFocusableOnElement,
  ToastMode,
  useToasts,
} from '@groma-dev/blueprint';
import { FormattedMessage } from 'react-intl';
import { SiteExp } from '@groma-dev/scaffold';
import { Link, useLocation } from 'react-router-dom';
import { useDataStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AuthSection } from './authsection/AuthSection';
import { BentoPanel } from './bentopanel/BentoPanel';
import { MobileMenu } from './mobilemenu/MobileMenu';
import styles from './Header.module.scss';

export enum HeaderMenu {
  ABOUT = 'about',
  ACCOUNT = 'account',
  INVEST = 'invest',
  PARTNER = 'partner',
  RENT = 'rent',
  SELL = 'sell',
}

export interface HeaderProps {}

const Header = observer(() => {
  const dataStore = useDataStore();
  const { addToast } = useToasts();
  const { pathname, search } = useLocation();
  const { Domains } = AppConfig.Settings;
  const loginRef = createRef<HTMLDivElement>();
  const elementsRef = useRef<NodeList>();
  let loginTimeout: NodeJS.Timeout;

  const [bentoPanelOpen, setBentoPanelOpen] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [submenuOpen, setSubmenuOpen] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  const handleToggleSubmenu = (value: boolean) => {
    setSubmenuOpen(value);
  };

  const handleShowLogin = () => {
    if (loginTimeout) clearTimeout(loginTimeout);
    setTimeout(() => setShowLogin(true), 150);
  };

  const handleHideLogin = (e: React.MouseEvent & React.FocusEvent<HTMLDivElement, Element>) => {
    if (e.relatedTarget instanceof Window || (loginRef.current && !loginRef.current?.contains(e.relatedTarget))) {
      loginTimeout = setTimeout(() => {
        setShowLogin(false);
      }, 225);
    }
  };

  const handleResize = () => {
    if (window.innerWidth >= 1024) {
      setMenuOpen(false);
      setSubmenuOpen(false);
    }
  };

  const signOutUser = async (e: React.MouseEvent<HTMLLinkElement | HTMLAnchorElement>) => {
    e.preventDefault();
    const response = await dataStore.signOutUser();

    if (response.success) {
      const returnUrl = encodeURIComponent(`${pathname}${search}`);
      // TODO: change site experience
      window.location.href = `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.HOLDINGS}`;
    } else {
      addToast(response.error?.message ?? 'something went wrong.', ToastMode.ERROR);
    }
  };

  useEffect(() => {
    if (menuOpen) {
      // when open, make all page elements -1
      const pageEls = getFocusableElements();
      elementsRef.current = pageEls;
      setFocusableOnElement(pageEls, -1);
      // reset appBar elements to tabbable
      setFocusableOnElement(getFocusableElements('appBar'), 0);
    } else {
      // make sure all page elements are tabbable when closed
      setFocusableOnElement(elementsRef.current, 0);
    }
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setSubmenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <AppBar
        id="appBar"
        className={classNames(styles.appBar, submenuOpen && styles.submenu)}
        position="fixed"
        testId="header">
        <Toolbar className={classNames(styles.toolbarWrapper)}>
          {submenuOpen ? (
            <NavItem
              as="button"
              className={classNames(styles.backButton)}
              onClick={() => setSubmenuOpen(false)}
              testId="menuBackButton">
              <ArrowLeftIcon />
              <FormattedMessage
                id="header.menu.backButton"
                description="Header menu back button text"
                defaultMessage="Main Menu"
              />
            </NavItem>
          ) : (
            <NavItem to="/profile" as={Link} aria-label="Full groma logo">
              <FullLightLogo className={classNames(styles.logo)} />
            </NavItem>
          )}
          <nav className={classNames(styles.navContainer)} {...getTestIdAttribute('nav')}></nav>
          <div className={classNames(styles.rightContainer)}>
            {dataStore.userState.user ? (
              <AuthSection
                handleOpenBento={() => setBentoPanelOpen(true)}
                user={dataStore.userState.user}
                signOutUser={signOutUser}
                menuOpen={menuOpen}
              />
            ) : (
              <div className={classNames(styles.loginContainer)}>
                <button
                  className={classNames(styles.signInButton)}
                  onBlur={handleHideLogin}
                  onClick={handleShowLogin}
                  onMouseEnter={handleShowLogin}
                  onMouseLeave={handleHideLogin}
                  {...getTestIdAttribute('signIn')}>
                  <FormattedMessage
                    id="header.account.signIn"
                    defaultMessage="Sign In"
                    description="Account Sign In Button"
                  />
                </button>
                <div
                  role="presentation"
                  onBlur={handleHideLogin}
                  onClick={handleShowLogin}
                  onMouseEnter={handleShowLogin}
                  onMouseLeave={handleHideLogin}
                  className={classNames(styles.loginPopup, showLogin && styles.visible)}
                  ref={loginRef}>
                  <a href={Domains.investors.baseUrl} className={classNames(styles.loginLink)}>
                    <FormattedMessage
                      id="header.account.popout.investorLink"
                      defaultMessage="Investor portal"
                      description="Account Popout investor portal link"
                    />
                  </a>
                  <a
                    href="https://groma.managebuilding.com/Resident/portal/login"
                    className={classNames(styles.loginLink)}>
                    <FormattedMessage
                      id="header.account.popout.tenantLink"
                      defaultMessage="Resident Center"
                      description="Account Popout resident center link"
                    />
                  </a>
                </div>
              </div>
            )}
            <div className={classNames(styles.menuButtonContainer)}>
              <button
                className={classNames(styles.menuButton)}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
                {...getTestIdAttribute('menuButton')}>
                {menuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </Toolbar>
        {dataStore.userState.user && <BentoPanel open={bentoPanelOpen} onClose={() => setBentoPanelOpen(false)} />}
        <MobileMenu
          open={menuOpen}
          submenuOpen={submenuOpen}
          toggleSubmenu={handleToggleSubmenu}
          signOutUser={signOutUser}
        />
      </AppBar>
      <Toolbar />
    </>
  );
});

export default Header;
